import {
  DataDisplay,
  NeutralTag,
  SectionSubHeading,
  Text,
} from "@certn/cascadia-react";
import {
  AdjudicationStatus,
  AdjudicationStatusTag,
} from "../../../utils/renderStatusTag";
import { useTranslation } from "react-i18next";
import { array, InferType, number, object, string } from "yup";
import { OutcomeSchema } from "../../adjudication/types";
import { css } from "@emotion/css";

type Props = {
  adjudicationResults: unknown;
};

const schema = array().of(
  object({
    category_key: string().required(),
    category_label: string().required(),
    codes: array()
      .of(
        object({
          key: string().required(),
          code_label: string().required(),
          label: string().required(),
          number_of_offenses: number().required(),
          years_since_offense: number().required(),
          outcome: OutcomeSchema.required(),
        })
      )
      .required(),
  })
);

const validateAdjudicationResults = (
  adjudicationResults: unknown
): InferType<typeof schema> | null => {
  try {
    return schema.validateSync(adjudicationResults);
  } catch (error) {
    console.error(error);
    return null;
  }
};

export function BasicCanadianCriminalRecordAdjudicationResults({
  adjudicationResults,
}: Props) {
  const { t } = useTranslation();

  const validatedAdjudicationResults =
    validateAdjudicationResults(adjudicationResults);

  if (!validatedAdjudicationResults) {
    return null;
  }

  return (
    <>
      {validatedAdjudicationResults.map((category) => (
        <>
          <SectionSubHeading
            title={category.category_label}
            key={category.category_key}
          />
          <div className={styles.dataDisplay}>
            <DataDisplay
              renderTopMargin={false}
              columns={[
                {
                  key: "code",
                  name: t("Crim. Code"),
                  isRowHeader: true,
                  render: ({ code }) => (
                    <NeutralTag type="subtle" size="medium">
                      {code}
                    </NeutralTag>
                  ),
                  width: "20%",
                },
                {
                  key: "offence",
                  name: t("Offence"),
                  isRowHeader: true,
                  render: ({ offence }) => (
                    <Text style="global/body-primary">{offence}</Text>
                  ),
                  width: "60%",
                },
                {
                  key: "outcome",
                  name: t("Adjusted score"),
                  render: ({ outcome }) =>
                    AdjudicationStatusTag(outcome as AdjudicationStatus),
                  width: "20%",
                },
              ]}
              rows={category.codes.map((code) => ({
                key: code.key,
                code: code.code_label,
                offence: code.label,
                outcome: code.outcome,
              }))}
            />
          </div>
        </>
      ))}
    </>
  );
}

const styles = {
  dataDisplay: css({
    paddingLeft: "var(--space-4)",
  }),
};
